import React from "react";
import { Modal } from "../../Components/Modal";
import type { MigrationUploadObject } from "./utils";
import { normalizeMigrationEntry } from "./utils";

interface MigrationUploadModalProps {
	show: boolean;
	onHide: () => void;
	uploadFile: File | null;
	uploadPreview: string | null;
	uploadError: string | null;
	uploadInfo: string | null;
	uploadParsed: Record<string, MigrationUploadObject> | null;
	uploading: boolean;
	onFileSelect: (file: File | null) => void;
	onImport: () => void;
}

export const MigrationUploadModal: React.FC<MigrationUploadModalProps> = ({
	show,
	onHide,
	uploadFile,
	uploadPreview,
	uploadError,
	uploadInfo,
	uploadParsed,
	uploading,
	onFileSelect,
	onImport
}) => {
	return (
		<Modal show={show} onHide={onHide} centered size="full85">
			<Modal.Header closeButton>
				<Modal.Title>Upload migration JSON</Modal.Title>
			</Modal.Header>
			<Modal.Body scrollable={false}>
				<div className="flex flex-col gap-6 lg:flex-row">
					<div className="min-w-[360px] max-w-[420px] space-y-4">
						<div>
							<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Upload</p>
							<h2 className="text-xl font-semibold text-slate-900">Select a .json file</h2>
							<p className="mt-2 text-sm text-slate-500">We only read the file locally and show a preview to help you validate the payload.</p>
						</div>
						<label className="text-xs font-semibold uppercase tracking-wide text-slate-500">File</label>
						<input
							type="file"
							accept=".json,application/json"
							className="mt-1 block w-full text-sm text-slate-600 file:mr-4 file:rounded-full file:border-0 file:bg-slate-900 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-slate-800"
							onChange={(event) => onFileSelect(event.target.files ? event.target.files[0] : null)}
						/>
						{uploadFile && <p className="text-xs text-slate-500">Selected: {uploadFile.name}</p>}
						{uploadError && <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{uploadError}</p>}
						<p className="text-xs text-slate-400">Accepted format: .json (UTF-8)</p>
					</div>
					<div className="flex-1 max-h-[70vh] overflow-auto rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4">
						{uploadPreview ? (
							<pre className="h-full w-full overflow-auto rounded-xl bg-white p-4 text-xs text-slate-800">
								{uploadPreview}
							</pre>
						) : (
							<div className="flex h-full min-h-[320px] flex-col items-center justify-center text-center text-sm text-slate-500">
								<p className="text-base font-semibold text-slate-700">No preview yet</p>
								<p className="mt-2 text-xs text-slate-500">Choose a JSON file on the left to render its content here.</p>
							</div>
						)}
						{uploadInfo && <p className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{uploadInfo}</p>}
					</div>
				</div>
			</Modal.Body>
			<Modal.Footer>
				<button
					type="button"
					onClick={onImport}
					disabled={!uploadParsed || uploading}
					className="rounded-full bg-slate-900 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
				>
					{uploading ? "Importing..." : "Import JSON"}
				</button>
				<button
					type="button"
					onClick={onHide}
					className="rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
				>
					Close
				</button>
			</Modal.Footer>
		</Modal>
	);
};

export { normalizeMigrationEntry };
export type { MigrationUploadObject };
